<template>
  <div class="flex flex-col w-full glass-frosted p-4 rounded-6xl h-64">
    <!-- 标题栏 -->
    <div class="flex items-start justify-between mb-3.5">
      <span class="text-l-medium text-black-white">{{ $t('home.config.gameApp.title') }}</span>
      <Button color="black" size="sm" class="h-8" @click="handleAdd">
        {{ $t('home.config.gameApp.addButton') }}
      </Button>
    </div>

    <!-- 轮播区域 -->
    <div ref="emblaRef" class="relative size-full overflow-hidden flex-1 py-2 box-border">
      <div class="flex gap-2 select-none px-15.5 size-full">
        <div
          v-for="app in apps"
          :key="app.id"
          class="relative flex-none size-40 group"
          @dragover.prevent
          @drop="handleDrop($event, app)"
          @mouseenter="hoverAppId = app.id"
          @mouseleave="hoverAppId = null"
        >
          <!-- 应用卡片主体 -->
          <Tooltip
            side="top"
            align="start"
            :side-offset="app.configList.length === 0 ? -2 : 2"
            :align-offset="-14"
          >
            <div
              class="size-full cursor-pointer rounded-2xl flex flex-col relative overflow-hidden transition-all duration-200 p-3.5 bg-black"
              :class="{ 'hover:ring-3 hover:ring-blue-light-active': app.configList.length }"
            >
              <!-- 背景图标 -->
              <img
                :src="app.icon"
                class="size-33 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                alt=""
                srcset=""
              />
              <div
                class="size-full absolute top-0 left-0 flex items-center justify-center bg-mask60"
              >
                <!-- 操作按钮 -->
                <div class="flex gap-2 justify-end absolute top-3.5 right-3.5">
                  <!-- 打开详情 -->
                  <Button
                    v-if="app.configList.length"
                    color="glass"
                    size="icon-xs"
                    shape="circle"
                    @click.stop="openAppConfigDialog(app.id)"
                  >
                    <Icon icon="Writing-Wireframe-Details" />
                  </Button>
                  <!-- 删除 -->
                  <Button
                    color="glass"
                    size="icon-xs"
                    shape="circle"
                    @click.stop="openDeleteDialog(app.id)"
                  >
                    <Icon icon="Function-Wireframe-Delete" />
                  </Button>
                </div>

                <!-- 应用名称 -->
                <span class="text-m-medium text-white p-2">{{ app.name }}</span>

                <!-- 底部小图标 -->
                <div
                  class="mt-auto flex justify-center absolute bottom-3.5 left-1/2 -translate-x-1/2"
                >
                  <div
                    v-for="(item, index) in app.configList"
                    :key="index"
                    class="flex items-center gap-2"
                  >
                    <Icon :icon="item.icon" class="size-6 text-white" />
                  </div>
                </div>
              </div>
            </div>
            <template #content>
              <div
                v-if="app.configList.length && hoverAppId === app.id"
                class="glass-s60 border-white rounded-2xl text-neutral-500 flex flex-col gap-0.5 min-w-48.5 w-fit"
              >
                <div
                  v-for="(item, index) in app.configList"
                  :key="index"
                  class="flex items-center gap-2 h-8 px-3 py-1.75"
                >
                  <Icon :icon="item.icon" class="size-4.5 text-black-white" />
                  <div class="flex items-center gap-1">
                    <span class="text-xs-medium text-black-white">{{ item.model }}</span>
                    <span class="text-xs-medium text-black-white">
                      {{ item.profile?.name || '' }}
                    </span>
                  </div>
                </div>
              </div>
              <template v-if="app.configList.length === 0 && hoverAppId === app.id">
                <div class="bg-black rounded-lg px-2 py-1 text-white">
                  {{ $t('home.config.gameApp.appEmptyConfigText') }}
                </div>
              </template>
            </template>
          </Tooltip>
        </div>

        <!-- 空占位 -->
        <div
          v-if="apps.length === 0"
          class="size-full flex items-center justify-center text-black-white text-text-m-medium bg-key rounded-6xl"
          @click="handleAdd"
        >
          {{ $t('home.config.gameApp.emptyText') }}
        </div>
      </div>

      <!-- 导航按钮 (当内容溢出时显示) -->
      <div class="absolute left-0 top-1/2 -translate-y-1/2 z-index-carousel">
        <Button
          size="icon-lg"
          shape="circle"
          color="glass"
          :disabled="!canScrollPrev"
          @click="scrollPrev"
        >
          <Icon icon="Direction-Wireframe-Left2" />
        </Button>
      </div>
      <div class="absolute right-0 top-1/2 -translate-y-1/2 z-index-carousel">
        <Button
          size="icon-lg"
          shape="circle"
          color="glass"
          :disabled="!canScrollNext"
          @click="scrollNext"
        >
          <Icon icon="Direction-Wireframe-Right2" />
        </Button>
      </div>
    </div>
    <Dialog
      v-model:open="showDeletModeDialog"
      :title="$t('home.config.gameApp.deleteDialog.title')"
      :description="$t('home.config.gameApp.deleteDialog.description')"
    >
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="black" @click="showDeletModeDialog = false">
            {{ $t('common.cancel') }}
          </Button>
          <Button color="text-b-w" @click="handleRemove">{{ $t('common.delete') }}</Button>
        </div>
      </template>
    </Dialog>
    <Dialog
      v-model:open="showAppConfigDialog"
      :title="$t('home.config.gameApp.detailDialog.title')"
      description=""
    >
      <ScrollArea v-if="appConfigById?.length" class="border-white text-neutral-500 h-42 pr-2">
        <div class="flex flex-col gap-2">
          <div v-for="(item, index) in appConfigById" :key="index" class="flex items-center gap-2">
            <div
              class="px-3 py-1.5 flex-1 flex gap-1.5 items-center bg-neutral-light-default rounded-4xl"
            >
              <Icon :icon="item.icon" class="size-4.5 text-text-glass-default" />
              <span class="text-xs-medium text-text-glass-default">{{ item.model }}</span>
              <span class="text-xs-medium text-text-glass-default">
                {{ item.profile?.name || '' }}
              </span>
            </div>
            <Button color="blue1" variant="frameless" shape="square" size="icon-sm">
              <Icon
                icon="Function-Wireframe-Delete"
                class="size-5.5 text-neutral-500 cursor-pointer"
                @click.stop="deleteConfig(item.model)"
              />
            </Button>
          </div>
        </div>
      </ScrollArea>
    </Dialog>
  </div>
</template>

<script lang="ts" setup>
  /**
   * @file GameApp.vue
   * @description 游戏/应用关联组件，支持拖拽配置关联
   * @created 2026-02-03
   */

  import { ref, onMounted, computed, watch } from 'vue'
  import emblaCarouselVue from 'embla-carousel-vue'
  import { storeToRefs } from 'pinia'
  import { Icon, Button, Tooltip, ScrollArea } from '@rapoo/element'
  import { useConfigurationStore } from '@/stores/configuration'
  import { useLocaleStore } from '@/stores'
  import { getDragData, DragType } from '@/utils/dnd'
  import { Dialog } from '@rapoo/element'

  const SCROLL_THRESHOLD = 8

  const configStore = useConfigurationStore()
  const { apps: rawApps, configs } = storeToRefs(configStore)

  const { t } = useLocaleStore()

  // 扩展应用信息，加入关联的配置名称
  const apps = computed(() => {
    return rawApps.value.map((app) => {
      const configList = []
      for (const model in app.linkedProfileList) {
        const linked = app.linkedProfileList?.[model]
        configList.push({
          model,
          profile: configs.value.find((config) => config.index === linked.index),
          type: linked.type,
          icon: {
            keyboard: 'Hardware-Wireframe-Keyboard',
            mouse: 'Hardware-Wireframe-Mouse',
            headphone: 'Hardware-Wireframe-Headphone',
          }[linked.type],
        })
      }
      return {
        ...app,
        configList,
      }
    })
  })

  // Embla Carousel
  const [emblaRef, emblaApi] = emblaCarouselVue({ align: 'start', containScroll: 'trimSnaps' })
  const canScrollPrev = ref(false)
  const canScrollNext = ref(false)

  const hoverAppId = ref<string | null>(null)

  watch(
    () => apps.value.length,
    (_len) => {
      if (!emblaApi.value) return
      emblaApi.value.reInit({
        align: 'start',
        containScroll: 'trimSnaps',
        watchDrag: contentOverflow.value,
      })
      updateScrollState()
    },
    { immediate: true },
  )

  const contentOverflow = computed(() => apps.value.length >= SCROLL_THRESHOLD)

  /**
   * 更新滚动状态
   */
  const updateScrollState = () => {
    if (!emblaApi.value) return
    canScrollPrev.value = emblaApi.value.canScrollPrev()
    canScrollNext.value = emblaApi.value.canScrollNext()
  }

  onMounted(() => {
    // 确保 emblaRef 被使用以通过 TS 检查
    void emblaRef.value

    if (emblaApi.value) {
      emblaApi.value.on('select', updateScrollState)
      emblaApi.value.on('reInit', updateScrollState)
      updateScrollState()
    }
  })

  const scrollPrev = () => {
    if (!canScrollPrev.value) return
    emblaApi.value?.scrollPrev()
  }
  const scrollNext = () => {
    if (!canScrollNext.value) return
    emblaApi.value?.scrollNext()
  }

  /**
   * 处理添加应用
   */
  const handleAdd = () => {
    window.electronAPI
      ?.selectFile({
        title: t('home.config.gameApp.selectFilePicker.title'),
        filters: [
          {
            name: t('home.config.gameApp.selectFilePicker.filterName'),
            extensions: ['exe', 'lnk'],
          },
        ],
        properties: ['openFile'],
      })
      .then(async ({ filePaths }) => {
        const [filePath] = filePaths
        if (filePath) {
          // 处理选中的文件路径
          const info = await window.electronAPI?.getFileInfo(filePath)
          const icon = await window.electronAPI?.getFileIcon(filePath)
          console.log(info, icon)

          const name =
            info?.name?.replace(/\.(exe|lnk)$/i, '') ||
            t('home.config.gameApp.selectFilePicker.unknownFile')
          const path = info?.path || filePath
          const iconData = icon?.icon || ''

          if (info?.success) {
            configStore.addApp(name, path, iconData)
            setTimeout(() => {
              emblaApi.value?.reInit()
              emblaApi.value?.scrollTo(apps.value.length - 1)
            }, 100)
          }
        }
      })
  }

  /**
   * 处理移除应用
   * @param id 应用ID
   */
  const deleteAppId = ref<string | null>(null)
  const showDeletModeDialog = ref(false)
  const openDeleteDialog = (id: string) => {
    deleteAppId.value = id
    showDeletModeDialog.value = true
  }
  const handleRemove = () => {
    configStore.removeApp(deleteAppId.value!)
    showDeletModeDialog.value = false
    deleteAppId.value = null
    setTimeout(() => emblaApi.value?.reInit(), 100)
  }

  /**
   * 处理应用配置
   * @param app 应用
   */
  const showAppConfigDialog = ref(false)
  const appConfigId = ref<string | null>(null)
  const appConfigById = computed(() => {
    return apps.value.find((app) => app.id === appConfigId.value)?.configList
  })
  const openAppConfigDialog = (appId: string) => {
    showAppConfigDialog.value = true
    appConfigId.value = appId
  }
  const deleteConfig = (model: string) => {
    configStore.unlinkProfileFromApp(appConfigId.value!, model)
    if (appConfigById.value?.length === 0) {
      showAppConfigDialog.value = false
      appConfigId.value = null
    }
  }

  /**
   * 处理拖拽放置
   * @param event 拖拽事件
   * @param app 目标应用
   */
  const handleDrop = (event: DragEvent, app: any) => {
    if (!event.dataTransfer) return

    const data = getDragData(event.dataTransfer, DragType.CONFIGURATION)
    if (data && data.id) {
      configStore.linkProfileToApp(app.id, data.id)
    }
  }
</script>

<style lang="scss" scoped>
  /* 隐藏滚动条但保留功能 (Embla 不需要这个，但为了保险) */
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
</style>
